import React, {Fragment as F} from 'react'
import css from './VehicleForm.less'
import CheckboxLabel from './CheckboxLabel'
import type {ForwardedState, OptionTuple, OptionTuples} from "../helpers/react-helpers";
import { forwardState, linkState, withClass} from "../helpers/react-helpers"
import ActionLink from './ActionLink'
import cn from 'classnames'
import Moment from 'moment'
import range from 'lodash/range'
import icons from '../../markons/markons'
import AioSelect from './aio-inputs/AioSelect'
import AioText from './aio-inputs/AioText'
import AioDatepicker from './aio-inputs/AioDatePicker'
import Icon from './Icon'
import {identity} from '../kb'
import AioTimePicker from './aio-inputs/AioTimePicker'

const MAP: Record<string, React.FC<ForwardedState>> = {
    RANGE: Range,
    DAILY: Daily,
    WEEKLY: Weekly,
    MONTHLY_DAY: MonthlyDay,
    MONTHLY_DOW: MonthlyDow,
    YEARLY: Yearly,
}


const InputRow = withClass('div', css.inputRow)
const InputLabel = withClass('div', css.inputLabel)

const OTHER_REASON = "__OTHER__"

export type BlockedDateBoxState = {
    id: number | null
    reason: string
    otherReason: string
    recurrenceType: string
    addTime: boolean
    startDate: string
    endYear: string
    startTime: string
    endDate: string
    endTime: string
    separationCount: number
    daysOfWeek: number
    dayOfWeek: number
    weekOfMonth: number
}

export const DEFAULT_BLOCKED_DATE_STATE = Object.freeze<BlockedDateBoxState>({
    id: null,
    reason: '',
    otherReason: '',
    recurrenceType: 'RANGE',
    addTime: false,
    startDate: '',
    endYear: '',
    startTime: '',
    endDate: '',
    endTime: '',
    separationCount: 1,
    daysOfWeek: 0,
    dayOfWeek: 0,
    weekOfMonth: 1,
})

export type BlockedDateChangeEvent = {value: BlockedDateBoxState}
export type BlockedDateChangeHandler = (ev: BlockedDateChangeEvent) => void

interface BlockedDateBoxProps {
    defaultValue?: Partial<BlockedDateBoxState>
    reasons: OptionTuples
    onDelete?: BlockedDateChangeHandler
    onChange?: BlockedDateChangeHandler
}

export default class BlockedDateBox extends React.Component<BlockedDateBoxProps, BlockedDateBoxState> {


    constructor(props: BlockedDateBoxProps) {
        super(props)
        this.state = {
            ...DEFAULT_BLOCKED_DATE_STATE,
            ...props.defaultValue,
        }
        const reasonSet = new Set(props.reasons.map((r: any) => r[1]))
        if(this.state.reason && !reasonSet.has(this.state.reason)) {
            this.state.otherReason = this.state.reason
            this.state.reason = OTHER_REASON
        }
    }

    render() {
        const reasons: OptionTuples = [...this.props.reasons.map(([,text]) => [text,text] as OptionTuple), [OTHER_REASON, "Other"]]

        return (
            <div className={css.dateBlock}>
                <InputRow>
                    <AioSelect testId="reason" label="Reason" {...linkState.call(this, 'reason')} options={reasons} placeholder="— Reason —"/>
                </InputRow>
                {this.state.reason === OTHER_REASON ? (
                    <InputRow>
                        <AioText testId="other-reason" label="Other Reason" {...linkState.call(this, 'otherReason')}/>
                    </InputRow>
                ) : null}
                <InputRow>
                    <AioSelect testId="recurrence-type" label="Recurrence Type" {...linkState.call(this, 'recurrenceType')}>
                        <option value="RANGE">Range</option>
                        <option value="DAILY">Daily</option>
                        <option value="WEEKLY">Weekly</option>
                        <option value="MONTHLY_DAY">Monthly, by day number</option>
                        <option value="MONTHLY_DOW">Monthly, by day of week</option>
                        <option value="YEARLY">Yearly</option>
                    </AioSelect>
                </InputRow>

                {MAP[this.state.recurrenceType] ? React.createElement(MAP[this.state.recurrenceType], forwardState(this)) : null}

                {this.props.onDelete ?
                    <ActionLink testId="delete" onClick={this.handleDelete} className={`delete-link ${css.topRight}`}>
                        <Icon name={icons.ionTrashA}/>
                    </ActionLink>
                    : null}
            </div>
        )
    }

    handleDelete = () => {
        this.props.onDelete?.({value: this.state})
    }


    componentDidUpdate(prevProps: any, prevState: any, snapshot: any) {
        // if(this.props.value !== prevProps.value) {
        //     this.setState(this.props.value);
        // }
        if(this.props.onChange && this.state !== prevState) {
            this.props.onChange({value: this.state})
        }
    }
}


function Range({state, setState, linkState, linkChecked}: ForwardedState) {
    return (
        <>
            {!state.addTime ? <InputRow>
                    {/*From <DatePicker {...linkState('startDate')}/> to <DatePicker {...linkState('endDate')}/>*/}
                    From <AioDatepicker testId="start-date" label="Start Date" {...linkState('startDate', identity)} inline width={140}/> to <AioDatepicker label="End Date" testId="end-date" {...linkState('endDate', identity)} inline width={140}/>
                </InputRow>
                : <F>
                    <InputRow>
                        From <AioDatepicker label="Start Date" testId="start-date" {...linkState('startDate', identity)} inline width={140}/>@<AioTimePicker militaryTime={false} label="Start Time" testId="start-time" {...linkState('startTime', ev => ev.value)} inline width={140}/>
                    </InputRow>
                    <InputRow>To <AioDatepicker label="End Date" testId="end-date" {...linkState('endDate', identity)} inline width={140}/>@<AioTimePicker militaryTime={false} label="End Time" testId="end-time" {...linkState('endTime', ev => ev.value)} inline width={140}/>
                    </InputRow>
                </F>}

            <InputRow>
                <CheckboxLabel testId="add-time" {...linkChecked('addTime')}>Add time</CheckboxLabel>
            </InputRow>
        </>
    )
}

function Daily({state, setState, linkState, linkChecked}: ForwardedState) {
    return <F>
        <InputRow>
            Starting on <AioDatepicker testId="start-date" label="Start Date" {...linkState('startDate', identity)} inline width={140}/> and ending on <AioDatepicker label="End Date" testId="end-date" {...linkState('endDate', identity)} inline width={140}/>
        </InputRow>
        {/*<InputRow>*/}
        {/*    Every <NumberBox min={1} max={365} {...linkState('separationCount')} width="80px"/> day{state.separationCount == 1 ? null : 's'}*/}
        {/*</InputRow>*/}
        {state.addTime ? <InputRow>
            Between <AioTimePicker militaryTime={false} label="Start Time" testId="start-time" {...linkState('startTime', ev => ev.value)} inline width={140}/> and <AioTimePicker militaryTime={false} label="End Time" testId="end-time" {...linkState('endTime', ev => ev.value)} inline width={140}/>
            {' '}<ActionLink testId="remove-time" onClick={() => setState('addTime', false)}>(remove)</ActionLink>
        </InputRow> : <InputRow>
            <ActionLink testId="add-time" onClick={() => setState('addTime', true)}>Add time...</ActionLink>
        </InputRow>}
    </F>
}

function DowBubble({children, checked, onChange}: any) {
    // see also: https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/checkbox_role
    return <label className={cn(css.dowBubble, checked ? css.dowChecked : css.dowUnchecked)}>
        <input type="checkbox" checked={checked} className={css.dowCheckbox} onChange={onChange}/>
        {children}
    </label>
}

function DaysOfWeekPicker({value: flags, onChange}: any) {

    function DOW({value, children}: any) {
        return (
            <DowBubble checked={(flags & value) === value} onChange={(ev: any) => onChange({
                value: ev.target.checked ? flags | value : flags & ~value,
            })}>{children}</DowBubble>
        )
    }

    return <div className={css.dowWrap}>
        <DOW value={1 << 0}>Su</DOW>
        <DOW value={1 << 1}>M</DOW>
        <DOW value={1 << 2}>Tu</DOW>
        <DOW value={1 << 3}>W</DOW>
        <DOW value={1 << 4}>Th</DOW>
        <DOW value={1 << 5}>F</DOW>
        <DOW value={1 << 6}>Sa</DOW>
    </div>
}

function Weekly({state, setState, linkState, linkChecked, getState}: ForwardedState) {
    return (
        <F>
            <InputRow>
                Starting on <AioDatepicker testId="start-date" label="Start Date" {...linkState('startDate', identity)} inline width={140}/> and ending
                on <AioDatepicker label="End Date" testId="end-date" {...linkState('endDate', identity)} inline width={140}/>
            </InputRow>
            <InputRow>
                <InputLabel>Days of week</InputLabel>
                <DaysOfWeekPicker value={getState('daysOfWeek', 0)}
                                  onChange={(ev: any) => setState('daysOfWeek', ev.value)}/>
            </InputRow>
            {/*<InputRow>*/}
            {/*    Every <NumberBox min={1} max={52} {...linkState('separationCount')} width="80px"/> week{state.separationCount == 1 ? null : 's'}*/}
            {/*</InputRow>*/}
            {state.addTime ? <InputRow>
                Between <AioTimePicker militaryTime={false} label="Start Time" testId="start-time" {...linkState('startTime', ev => ev.value)} inline width={140}/> and <AioTimePicker militaryTime={false} label="End Time" testId="end-time" {...linkState('endTime', ev => ev.value)} inline width={140}/>
                {' '}<ActionLink testId="remove-time" onClick={() => setState('addTime', false)}>(remove)</ActionLink>
            </InputRow> : <InputRow>
                <ActionLink testId="add-time" onClick={() => setState('addTime', true)}>Add time...</ActionLink>
            </InputRow>}
        </F>
    )
}

function MonthlyDay({state, setState, linkState, linkChecked, getState}: ForwardedState) {
    return <F>
        <InputRow>
            Starting on <AioDatepicker testId="start-date" label="Start Date" {...linkState('startDate', identity)} inline width={140}/> and ending on <AioDatepicker label="End Date" testId="end-date" {...linkState('endDate', identity)} inline width={140}/>
        </InputRow>
        <InputRow>
            On the <AioSelect testId="day-of-month" label="Day" {...linkState('dayOfMonth')} inline>
            <option value={1}>1st</option>
            <option value={2}>2nd</option>
            <option value={3}>3rd</option>
            <option value={4}>4th</option>
            <option value={5}>5th</option>
            <option value={6}>6th</option>
            <option value={7}>7th</option>
            <option value={8}>8th</option>
            <option value={9}>9th</option>
            <option value={10}>10th</option>
            <option value={11}>11th</option>
            <option value={12}>12th</option>
            <option value={13}>13th</option>
            <option value={14}>14th</option>
            <option value={15}>15th</option>
            <option value={16}>16th</option>
            <option value={17}>17th</option>
            <option value={18}>18th</option>
            <option value={19}>19th</option>
            <option value={20}>20th</option>
            <option value={21}>21st</option>
            <option value={22}>22nd</option>
            <option value={23}>23rd</option>
            <option value={24}>24th</option>
            <option value={25}>25th</option>
            <option value={26}>26th</option>
            <option value={27}>27th</option>
            <option value={28}>28th</option>
            {/*<option value={-3}>3rd to last</option>*/}
            {/*<option value={-2}>2nd to last</option>*/}
            <option value={-1}>last</option>
        </AioSelect> day of the month
        </InputRow>
        {/*<InputRow>*/}
        {/*    Every <NumberBox min={1} max={36} {...linkState('separationCount')} width="80px"/> month{state.separationCount == 1 ? null : 's'}*/}
        {/*</InputRow>*/}
        {state.addTime ? <InputRow>
            Between <AioTimePicker militaryTime={false} label="Start Time" testId="start-time" {...linkState('startTime', ev => ev.value)} inline width={140}/> and <AioTimePicker militaryTime={false} label="End Time" testId="end-time" {...linkState('endTime', ev => ev.value)} inline width={140}/>
            {' '}<ActionLink testId="remove-time" onClick={() => setState('addTime', false)}>(remove)</ActionLink>
        </InputRow> : <InputRow>
            <ActionLink testId="add-time" onClick={() => setState('addTime', true)}>Add time...</ActionLink>
        </InputRow>}
    </F>
}

function MonthlyDow({state, setState, linkState, linkChecked, getState}: ForwardedState) {
    return <F>
        <InputRow>
            Starting on <AioDatepicker label="Start Date" testId="start-date" {...linkState('startDate', identity)} inline width={140}/> and ending on <AioDatepicker label="End Date" testId="end-date" {...linkState('endDate', identity)} inline width={140}/>
        </InputRow>
        <InputRow>
            On the <AioSelect label="N-th" {...linkState('weekOfMonth')} inline>
            <option value={1}>1st</option>
            <option value={2}>2nd</option>
            <option value={3}>3rd</option>
            <option value={4}>4th</option>
            <option value={-1}>last</option>
        </AioSelect> <AioSelect label="Day of Week" {...linkState('dayOfWeek')} inline>
            <option value={0}>Sunday</option>
            <option value={1}>Monday</option>
            <option value={2}>Tuesday</option>
            <option value={3}>Wednesday</option>
            <option value={4}>Thursday</option>
            <option value={5}>Friday</option>
            <option value={6}>Saturday</option>
        </AioSelect> of the month
        </InputRow>
        {/*<InputRow>*/}
        {/*    Every <NumberBox min={1} max={36} {...linkState('separationCount')} width="80px"/> month{state.separationCount == 1 ? null : 's'}*/}
        {/*</InputRow>*/}
        {state.addTime ? <InputRow>
            Between <AioTimePicker militaryTime={false} label="Start Time" testId="start-time" {...linkState('startTime', ev => ev.value)} inline width={140}/> and <AioTimePicker militaryTime={false} label="End Time" testId="end-time" {...linkState('endTime', ev => ev.value)} inline width={140}/>
            {' '}<ActionLink testId="remove-time" onClick={() => setState('addTime', false)}>(remove)</ActionLink>
        </InputRow> : <InputRow>
            <ActionLink testId="add-time" onClick={() => setState('addTime', true)}>Add time...</ActionLink>
        </InputRow>}
    </F>
}

function Yearly({state, setState, linkState, linkChecked, getState}: ForwardedState) {
    const startDate = Moment(state.startDate, 'YYYY-MM-DD')

    return <F>
        <InputRow>
            Starting on <AioDatepicker label="Start Date" testId="start-date" {...linkState('startDate', identity)} inline width={140}/> and ending in <AioSelect testId="year" label="Year" inline {...linkState('endYear')} width="80px" disabled={!startDate.isValid()}>
            <option hidden value="">(year)</option>
            {startDate.isValid() ? range(startDate.year(), startDate.year() + 31).map(y => <option
                key={y}>{y}</option>) : null}
        </AioSelect>
        </InputRow>
        {startDate.isValid() ? <InputRow>
            On {startDate.format('MMMM Do')}
        </InputRow> : <InputRow>Please choose a start date.</InputRow>}
        {/*<InputRow>*/}
        {/*    Every <NumberBox min={1} max={10} {...linkState('separationCount')} width="80px"/> year{state.separationCount == 1 ? null : 's'}*/}
        {/*</InputRow>*/}
        {state.addTime ? <InputRow>
            Between <AioTimePicker militaryTime={false} label="Start Time" testId="start-time" {...linkState('startTime', ev => ev.value)} inline width={140}/> and <AioTimePicker militaryTime={false} label="End Time" testId="end-time" {...linkState('endTime', ev => ev.value)} inline width={140}/>
            {' '}<ActionLink testId="remove-time" onClick={() => setState('addTime', false)}>(remove)</ActionLink>
        </InputRow> : <InputRow>
            <ActionLink testId="add-time" onClick={() => setState('addTime', true)}>Add time...</ActionLink>
        </InputRow>}
    </F>
}
